import { defineStyle } from '@chakra-ui/react';

export const colors = {
  palette_gray: {
    100: '#202532',
    80: '#50545E',
    70: '#6A6E77',
    60: '#838791',
    40: '#BEC0C5',
    10: '#D9DADD',
    5: '#EAEAEB',
    0: '#F2F2F3'
  },
  palette_primary: {
    125: '#A5274D',
    100: '#D44A74',
    0: '#F7DEE5'
  },
  palette_white: '#FFFFFF',
  palette_black: '#000000',
  palette_success: {
    100: '#086343',
    50: '#008556',
    5: '#D6F3E2'
  },
  palette_warning: {
    100: '#A64F21',
    50: '#E86825',
    10: '#FFE1BE',
    5: '#FFF6EC'
  },
  palette_alert: {
    100: '#9F1B1F',
    50: '#DE1C22',
    5: '#FFD6D7',
    0: '#FCF0F4'
  },
  palette_blue: {
    100: '#5353F6',
    20: '#E8F3FE',
    10: '#F3F1FE',
    5: '#DFDFFB',
    0: '#F3F1FE'
  },

  blue: {
    50: '#E7E7FE',
    100: '#BBBBFB',
    200: '#9090F9',
    300: '#6464F7',
    400: '#3838F5',
    500: '#0D0DF2',
    600: '#0A0AC2',
    700: '#080891',
    800: '#050561',
    900: '#030330'
  },
  alert: {
    50: '#FBE9EA',
    100: '#F5C2C3',
    200: '#EE9B9D',
    300: '#E77377',
    400: '#E14C51',
    500: '#DA252A',
    600: '#AE1E22',
    700: '#831619',
    800: '#570F11',
    900: '#2C0708'
  },
  warning: {
    50: '#FBF0EA',
    100: '#F3D4C3',
    200: '#ECB89D',
    300: '#E49D77',
    400: '#DC8151',
    500: '#D5652A',
    600: '#AA5122',
    700: '#803D19',
    800: '#552811',
    900: '#2B1408'
  },
  success: {
    50: '#E7FDF5',
    100: '#BDFAE4',
    200: '#92F6D3',
    300: '#68F3C2',
    400: '#3EEFB1',
    500: '#13ECA0',
    600: '#0FBD80',
    700: '#0B8E60',
    800: '#085E40',
    900: '#042F20'
  },
  gray: {
    50: '#EFF1F5',
    100: '#D3D8E3',
    200: '#B7BFD1',
    300: '#9BA5BF',
    500: '#64739B',
    600: '#505C7C',
    700: '#3C455D',
    800: '#282E3E',
    900: '#14171F'
  },
  primary: {
    50: '#FAEAEF',
    100: '#F1C5D3',
    200: '#E9A0B6',
    300: '#E07B99',
    400: '#D7567D',
    500: '#CE3160',
    600: '#A5274D',
    700: '#7C1D3A',
    800: '#531426',
    900: '#290A13'
  }
};

export const descriptiveTokens = {
  pink: {
    50: '#FFF4F8FF',
    100: '#FCF0F4FF',
    200: '#F7DEE5FF',
    300: '#EF9DB6FF',
    400: '#EF6E95FF',
    500: '#ED3E73FF',
    600: '#D44A74FF',
    700: '#BE4268FF',
    800: '#A5274DFF',
    999: '#2D0B15FF'
  },
  blue: {
    50: '#F9F8FFFF',
    100: '#F3F1FEFF',
    200: '#DFDFFBFF',
    300: '#A8A9F9FF',
    400: '#7F7EF6FF',
    500: '#5353F6FF'
  },
  gray: {
    0: '#FFFFFFFF',
    100: '#F7F7F8FF',
    200: '#EAEAEBFF',
    300: '#D9DADDFF',
    400: '#BEC0C5FF',
    500: '#838791FF',
    600: '#6A6E77FF',
    700: '#50545EFF',
    800: '#202532FF',
    900: '#0F0D28FF'
  },
  success: {
    100: '#EBF9F1FF',
    200: '#D6F3E2FF',
    500: '#008556FF',
    700: '#086343FF'
  },
  warning: {
    100: '#FFF3E5FF',
    200: '#FFE1BEFF',
    500: '#E86825FF',
    700: '#A64F21FF'
  },
  alert: {
    100: '#FFEBEBFF',
    200: '#FFD6D7FF',
    500: '#DE1C22FF',
    700: '#9F1B1FFF'
  },
  purple: {
    50: '#FDFCFFFF',
    100: '#F3F1FEFF',
    200: '#D8CCFFFF',
    300: '#BDA9FEFF',
    400: '#A78CFEFF',
    500: '#8762FEFF',
    600: '#7B53FEFF',
    700: '#6729EAFF',
    800: '#5813C8FF',
    999: '#220A35FF',
    900: '#320F4DFF'
  }
};

export const semanticColors = {
  background: {
    default: descriptiveTokens.gray[0],
    inactive: descriptiveTokens.gray[200],
    surface: descriptiveTokens.gray[100],
    container: {
      success: descriptiveTokens.success[200],
      warning: descriptiveTokens.warning[200],
      alert: descriptiveTokens.alert[200],
      primary: descriptiveTokens.pink[100],
      secondary: descriptiveTokens.blue[100]
    },
    status: {
      default: {
        alert: descriptiveTokens.alert[100],
        success: descriptiveTokens.success[100],
        warning: descriptiveTokens.warning[100],
        selected: descriptiveTokens.blue[100]
      },
      success: {
        default: descriptiveTokens.success[100]
      },
      warning: {
        default: descriptiveTokens.warning[100]
      },
      prominent: {
        alert: descriptiveTokens.alert[500],
        success: descriptiveTokens.success[500],
        warning: descriptiveTokens.warning[500],
        selected: descriptiveTokens.blue[500]
      }
    },
    primary: {
      default: descriptiveTokens.pink[600],
      hover: descriptiveTokens.pink[700],
      pressed: descriptiveTokens.pink[800]
    },
    secondary: {
      default: descriptiveTokens.gray[0],
      hover: descriptiveTokens.pink[100],
      pressed: descriptiveTokens.pink[100]
    },
    tertiary: {
      default: descriptiveTokens.gray[0],
      hover: descriptiveTokens.pink[100],
      pressed: descriptiveTokens.pink[100]
    }
  },
  border: {
    default: descriptiveTokens.gray[300],
    divider: descriptiveTokens.gray[200],
    hover: descriptiveTokens.gray[400],
    inactive: descriptiveTokens.gray[400],
    active: descriptiveTokens.pink[600],
    selected: descriptiveTokens.blue[500],
    status: {
      success: descriptiveTokens.success[500],
      warning: descriptiveTokens.warning[500],
      alert: descriptiveTokens.alert[500]
    },
    secondary: {
      default: descriptiveTokens.pink[600],
      hover: descriptiveTokens.pink[700],
      pressed: descriptiveTokens.pink[800]
    }
  },
  text: {
    default: descriptiveTokens.gray[600],
    prominent: descriptiveTokens.gray[800],
    active: descriptiveTokens.blue[500],
    inactive: descriptiveTokens.gray[500],
    link: descriptiveTokens.blue[500],
    highlight: descriptiveTokens.blue[500],
    inverted: descriptiveTokens.gray[0],
    status: {
      success: descriptiveTokens.success[500],
      onSuccessDefaultContainer: descriptiveTokens.success[700],
      onSuccessProminentContainer: descriptiveTokens.gray[0],
      warning: descriptiveTokens.warning[500],
      onWarningDefaultContainer: descriptiveTokens.warning[700],
      onWarningProminentContainer: descriptiveTokens.gray[0],
      alert: descriptiveTokens.alert[500],
      onAlertDefaultContainer: descriptiveTokens.alert[700],
      onAlertProminentContainer: descriptiveTokens.gray[0],
      selected: descriptiveTokens.blue[500],
      onSelectedProminentContainer: descriptiveTokens.gray[0]
    },
    onprimary: {
      default: descriptiveTokens.gray[0]
    },
    onsecondary: {
      default: descriptiveTokens.pink[600],
      pressed: descriptiveTokens.pink[800],
      hover: descriptiveTokens.pink[700]
    },
    onTertiary: {
      default: descriptiveTokens.pink[600],
      pressed: descriptiveTokens.pink[800],
      hover: descriptiveTokens.pink[700]
    },
    gradient: {
      blue: descriptiveTokens.blue[500],
      purple: descriptiveTokens.purple[500]
    }
  },
  icon: {
    default: descriptiveTokens.gray[600],
    prominent: descriptiveTokens.gray[800],
    inactive: descriptiveTokens.gray[500],
    link: descriptiveTokens.blue[500],
    highlight: descriptiveTokens.blue[500],
    status: {
      success: descriptiveTokens.success[500],
      onSuccessContainer: descriptiveTokens.success[700],
      warning: descriptiveTokens.warning[500],
      onWarningContainer: descriptiveTokens.warning[700],
      alert: descriptiveTokens.alert[500],
      onAlertContainer: descriptiveTokens.alert[700]
    },
    onsecondary: {
      default: descriptiveTokens.pink[600],
      pressed: descriptiveTokens.pink[800],
      hover: descriptiveTokens.pink[700]
    },
    onprimary: {
      inverted: descriptiveTokens.gray[0]
    },
    gradient: {
      blue: descriptiveTokens.blue[500],
      purple: descriptiveTokens.purple[500]
    }
  }
};

const defaultFonts = "'Poppins', Arial, sans-serif, 'Noto Color Emoji'";
export const fonts = {
  body: defaultFonts,
  heading: defaultFonts,
  mono: defaultFonts
};

export const spacing = {
  space_unit: {
    x1: '8px',
    x2: '16px',
    x3: '24px',
    x4: '32px',
    x5: '40px',
    x6: '52px',
    x8: '64px',
    x16: '128px'
  }
};

export const borderRadius = {
  radii: {
    x1: '4px',
    x2: '8px',
    x3: '12px',
    x4: '16px',
    infinite: '1000px'
  }
};

const h100 = defineStyle({
  fontSize: '12px',
  fontWeight: 600,
  lineHeight: '16px'
});

const h200 = defineStyle({
  fontSize: '14px',
  fontWeight: 600,
  lineHeight: '20px'
});

const h300 = defineStyle({
  fontSize: '16px',
  fontWeight: 600,
  lineHeight: '24px'
});

const h400 = defineStyle({
  fontSize: '20px',
  fontWeight: 700,
  lineHeight: '32px'
});

const h500 = defineStyle({
  fontSize: '24px',
  fontWeight: 700,
  lineHeight: '36px'
});

// FIXME: This font doesn't exist in the design systen,
// but is used in design files and throughout the app.
const bodyExtraExtraLarge = defineStyle({
  fontSize: '24px',
  fontWeight: 400,
  lineHeight: '32px'
});

// FIXME: This font doesn't exist in the design systen,
// but is used in design files and throughout the app.
const bodyExtraLarge = defineStyle({
  fontSize: '18px',
  fontWeight: 400,
  lineHeight: '28px'
});

const bodyLarge = defineStyle({
  fontSize: '16px',
  fontWeight: 400,
  lineHeight: '24px'
});

const bodyMedium = defineStyle({
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '20px'
});

const bodySmall = defineStyle({
  fontSize: '12px',
  fontWeight: 400,
  lineHeight: '16px'
});

const bodyCapital = defineStyle({
  fontSize: '16px',
  fontWeight: 400,
  lineHeight: '16px',
  textTransform: 'uppercase' as const
});

export const textStyles = {
  h100,
  h200,
  h300,
  h400,
  h500,
  bodyExtraLarge,
  bodyExtraExtraLarge,
  bodyLarge,
  bodyMedium,
  bodySmall,
  bodyCapital
};
export type TextStyle = keyof typeof textStyles;
